import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useApolloClient } from '@apollo/client';
import { useLocation } from '@tanstack/react-location';
import { Button, Form, message, Modal, Steps } from 'antd';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ModalEnum,
  useGetMyPaywallStatusQuery,
  useSelectPaywallPlanMutation,
} from '@/generated/graphql';
import useWidthAndHeight from '@/hooks/getWidthAndHeight';
import useUser from '@/hooks/useUser';

import BillingInformation from './BillingInformation';
import PlanSelection from './PlanSelection';

function PaywallModal(): React.ReactElement {
  const { isMobile } = useWidthAndHeight();
  const user = useUser();
  const apolloClient = useApolloClient();
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(true);

  const location = useLocation();
  const pathname = location.current.pathname;

  const { t } = useTranslation();

  const { data: paywallData, loading: paywallLoading } =
    useGetMyPaywallStatusQuery();
  const [selectPlan, { loading: planLoading }] = useSelectPaywallPlanMutation({
    onCompleted: async () => {
      Modal.success({
        title: 'Tack!',
        content: (
          <>
            Din prenumeration har aktiverats och du kan nu fortsätta att använda
            Portally! <br />
            <br />
            Du hittar din valda prenumeration under Kundinställningar.
          </>
        ),
        okText: 'OK',
      });
      await apolloClient.reFetchObservableQueries();
    },
    onError: error => {
      message.error(error.message);
    },
  });

  const paywallStatus = paywallData?.myPaywallStatus;
  const isTrialExpired = paywallStatus?.isTrialExpired;
  const daysLeft = paywallStatus?.daysLeftInTrial;
  const canDismiss = !isTrialExpired && daysLeft > 0;

  // Function to handle modal dismissal
  const handleDismiss = () => {
    // Set dismissal time until midnight
    const now = new Date();
    const midnight = new Date();
    midnight.setDate(now.getDate() + 1);
    midnight.setHours(0, 0, 0, 0);

    localStorage.setItem('paywallDismissedUntil', midnight.toISOString());
    setIsModalVisible(false);
  };

  // Function to manually open the modal
  const handleOpenModal = () => {
    setIsModalVisible(true);
  };

  // Check localStorage on component mount and when canDismiss changes
  useEffect(() => {
    if (canDismiss) {
      const dismissedUntil = localStorage.getItem('paywallDismissedUntil');
      if (dismissedUntil) {
        const dismissedDate = new Date(dismissedUntil);
        const now = new Date();
        if (now < dismissedDate) {
          setIsModalVisible(false);
        }
      }
    }
  }, [canDismiss]);

  // Don't show modal if we're on the terms page
  if (pathname === '/terms') {
    return null;
  }

  if (paywallLoading || !paywallData?.myPaywallStatus) {
    return null;
  }

  // Don't show modal if user has selected a plan
  if (paywallStatus?.selectedPlan) {
    return null;
  }

  // Show custom badge if modal was dismissed but trial is still active
  if (!isModalVisible && canDismiss) {
    return (
      <div
        style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          zIndex: 1000,
          cursor: 'pointer',
          backgroundColor: '#ff7875',
          color: 'white',
          padding: '12px 16px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '14px',
          fontWeight: 500,
          minWidth: '140px',
          transition: 'all 0.3s ease',
        }}
        onClick={handleOpenModal}
        onMouseEnter={e => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.25)';
        }}
        onMouseLeave={e => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.2)';
        }}
      >
        <ExclamationCircleOutlined style={{ fontSize: '16px' }} />
        <div
          style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}
        >
          <span>Välj abonnemang</span>
        </div>
      </div>
    );
  }

  // Don't show anything if modal is dismissed and trial is expired
  if (!isModalVisible) {
    return null;
  }

  const handlePlanSelect = (plan: string) => {
    setSelectedPlan(plan);
    setCurrentStep(1);
  };

  const handleBillingSubmit = async (billingInfo: any) => {
    if (!selectedPlan) {
      return;
    }

    await selectPlan({
      variables: {
        input: {
          clientId: user.activeClientId,
          selectedPlan,
          billingInfo,
        },
      },
    });
  };

  return (
    <Modal
      open={true}
      footer={null}
      closable={canDismiss}
      onCancel={canDismiss ? handleDismiss : undefined}
      width={800}
      title={isTrialExpired ? 'Prenumeration krävs' : 'Välj din prenumeration'}
    >
      {!isMobile && (
        <Steps
          current={currentStep}
          style={{ marginBottom: 24 }}
          items={[
            { title: 'Välj paket' },
            { title: 'Faktureringsinformation' },
          ]}
        />
      )}

      {currentStep === 0 && (
        <PlanSelection
          paywallStatus={paywallStatus}
          onPlanSelect={handlePlanSelect}
          isTrialExpired={isTrialExpired}
          daysLeft={daysLeft}
        />
      )}

      {currentStep === 1 && (
        <BillingInformation
          form={form}
          selectedPlan={selectedPlan}
          paywallStatus={paywallStatus}
          user={user}
          onSubmit={handleBillingSubmit}
          onBack={() => setCurrentStep(0)}
          loading={planLoading}
        />
      )}

      {currentStep === 0 && canDismiss && (
        <div className="flex justify-center mt-4">
          <Button
            onClick={handleDismiss}
            //  icon={<RotateRight size={14} className="mr-2" />}
          >
            Välj senare
          </Button>
        </div>
      )}
    </Modal>
  );
}

export default PaywallModal;
